import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';

// Flat blue palette — matches the redesigned app (no gradients/glass/glows).
const C_BG = '#CDE7F5';
const C_CARD = '#FFFFFF';
const C_BORDER = '#E3ECF5';
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_ACCENT = '#0077B6';
const C_TINT = '#E6F1FB';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Incorrect email or password. Please try again.');
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    setError('');
    setResetMessage('');
    if (!email) {
      setError('Please enter your email first.');
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMessage('We sent a password reset link to your email. Please also check your spam folder.');
    } catch (err) {
      setError('We couldn\'t send the email. Please check that the address is correct.');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: C_BG,
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    }}>
      <style>{`
        .login-card { width: 100%; max-width: 420px; }
        @media (max-width: 480px) {
          .login-card { max-width: 380px; padding: 36px 28px !important; }
        }
        .login-input::placeholder { color: ${C_TEXT_2}; }
      `}</style>

      {/* Login card */}
      <div
        className="login-card"
        style={{
          background: C_CARD,
          border: `1px solid ${C_BORDER}`,
          borderRadius: '20px',
          padding: '48px 40px',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(3,4,94,0.06)',
          zIndex: 10,
        }}
      >
        {/* Logo */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px',
        }}>
          <div style={{
            width: 96, height: 96, borderRadius: 20,
            background: C_TINT,
            border: `1px solid ${C_BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <img
              src={require('../assets/fenceflow-logo.png')}
              alt="Fence Flow"
              style={{ width: '72px', height: '72px', objectFit: 'contain' }}
            />
          </div>
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: 800,
          textAlign: 'center',
          color: C_TEXT,
          marginBottom: '8px',
          marginTop: 0,
          letterSpacing: '-0.5px',
        }}>
          Fence Flow
        </h1>
        <p style={{
          textAlign: 'center',
          color: C_TEXT_2,
          fontSize: '12px',
          marginBottom: '36px',
          marginTop: 0,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}>
          Point of Sale System
        </p>

        {/* Form */}
        <form onSubmit={handleLogin}>
          {/* Email */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              color: C_TEXT_2,
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginBottom: '8px',
              display: 'block',
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="login-input"
              style={{
                width: '100%',
                padding: '14px 16px',
                background: '#FFFFFF',
                border: `1px solid ${C_BORDER}`,
                borderRadius: '12px',
                color: C_TEXT,
                fontSize: '15px',
                outline: 'none',
                transition: 'border-color 0.3s, box-shadow 0.3s',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = C_ACCENT;
                e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C_BORDER;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              color: C_TEXT_2,
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginBottom: '8px',
              display: 'block',
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="login-input"
              style={{
                width: '100%',
                padding: '14px 16px',
                background: '#FFFFFF',
                border: `1px solid ${C_BORDER}`,
                borderRadius: '12px',
                color: C_TEXT,
                fontSize: '15px',
                outline: 'none',
                transition: 'border-color 0.3s, box-shadow 0.3s',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = C_ACCENT;
                e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C_BORDER;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Forgot password link */}
          <div style={{ textAlign: 'right', marginTop: '-12px', marginBottom: '20px' }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={loading}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: C_ACCENT,
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                fontFamily: 'inherit',
              }}
            >
              Forgot password?
            </button>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: '#FBECEC',
              border: '1px solid #E9C3C3',
              color: '#A32D2D',
              padding: '12px 16px',
              borderRadius: '10px',
              fontSize: '14px',
              marginBottom: '20px',
            }}>
              {error}
            </div>
          )}

          {/* Reset success message */}
          {resetMessage && (
            <div style={{
              background: '#EAF7EE',
              border: '1px solid #B7E0C2',
              color: '#2D7A3E',
              padding: '12px 16px',
              borderRadius: '10px',
              fontSize: '14px',
              marginBottom: '20px',
            }}>
              {resetMessage}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: C_ACCENT,
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'transform 0.3s, box-shadow 0.3s',
              boxShadow: '0 4px 14px rgba(0,119,182,0.3)',
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,119,182,0.45)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,119,182,0.3)';
            }}
          >
            {loading ? 'Signing in...' : 'Sign In →'}
          </button>
        </form>

        {/* Footer note */}
        <p style={{
          textAlign: 'center',
          color: C_TEXT_2,
          fontSize: '12px',
          marginTop: '32px',
          marginBottom: 0,
          letterSpacing: '0.5px',
        }}>
          Authorized personnel only
        </p>
      </div>

      {/* Bottom company info */}
      <div style={{
        position: 'absolute',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        color: C_TEXT_2,
        fontSize: '11px',
        textAlign: 'center',
        zIndex: 10,
        width: '90%',
        maxWidth: '500px',
      }}>
        <p style={{ margin: 0 }}>1803 Dorsey Rd, Hanover MD 21076 · 410-796-4747</p>
        <p style={{ marginTop: '4px', marginBottom: 0 }}>www.atlanticfencesupply.com</p>
      </div>
    </div>
  );
}

export default Login;
